import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, KeyRound } from "lucide-react";
import PasswordStrength from "@/components/PasswordStrength";

export default function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) return toast.error("Password must be at least 8 characters");
    if (password !== confirm) return toast.error("Passwords do not match");
    setBusy(true);
    try {
      await api.post("/auth/reset-password", { token, password });
      setDone(true);
      toast.success("Password updated — please sign in");
      setTimeout(() => nav("/login"), 1200);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reset failed");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-6">
      <div className="w-full max-w-md space-y-6" data-testid="reset-password-page">
        <div className="text-center">
          <div className="mx-auto w-14 h-14 rounded-full bg-[#F3F7FC] border border-[#BFCFE8] grid place-items-center">
            <KeyRound className="w-6 h-6 text-[#003DA5]" />
          </div>
          <h1 className="mt-4 text-3xl font-heading font-bold text-[#001F54]">Reset your password</h1>
          <p className="mt-2 text-slate-600 text-sm">Choose a new password for your MLILY CRM account.</p>
        </div>
        {!token && <div className="p-4 rounded-lg bg-red-50 border border-red-200 text-sm text-red-700">Missing reset token. Please use the link from your email.</div>}
        {done ? (
          <div className="p-4 rounded-lg bg-emerald-50 border border-emerald-200 text-sm text-emerald-700 flex items-center gap-2">
            <CheckCircle2 className="w-4 h-4" /> Password updated. Redirecting to sign in…
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-slate-500">New password</label>
              <input type="password" required minLength={8} value={password} onChange={(e) => setPassword(e.target.value)}
                className="mt-1 w-full h-12 px-4 border border-slate-300 rounded-lg outline-none" placeholder="At least 8 characters" data-testid="new-password" />
              <PasswordStrength password={password} />
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider font-semibold text-slate-500">Confirm password</label>
              <input type="password" required value={confirm} onChange={(e) => setConfirm(e.target.value)}
                className="mt-1 w-full h-12 px-4 border border-slate-300 rounded-lg outline-none" data-testid="confirm-password" />
            </div>
            <button type="submit" disabled={busy || !token} className="mlily-btn w-full h-12 rounded-lg" data-testid="reset-submit-btn">
              {busy ? "Updating…" : "Update password"}
            </button>
          </form>
        )}
        <div className="text-center text-sm text-slate-500">
          <Link to="/login" className="text-[#003DA5] font-semibold hover:underline">Back to sign in</Link>
        </div>
      </div>
    </div>
  );
}
